import type { EncounterProvider, PatientEncounter } from '@/api/patientsApi'

/**
 * Display rules for Epic visit history — the Patient Visit History tab.
 *
 * Pure and dependency-free, and here rather than in the component, because
 * `vitest.config.ts` is `environment: 'node'` with `include: ['src/**\/*.test.ts']`:
 * a `.tsx` test would silently never run. Every value the tab renders that needs
 * a decision made about it gets made here, under test.
 */

/**
 * How much of an HL7 timestamp is real.
 *
 * The emitter carries this beside every timestamp because `admittedAt` is derived
 * from a ZONE-LESS wall clock read as UTC. A `day`-precision admit is stored as
 * midnight UTC, and midnight is padding — printing "12:00 AM" would state a time
 * Epic never sent.
 */
type Precision = PatientEncounter['admittedAtPrecision']

/** Precisions that carry a real clock time, not just a calendar date. */
const HAS_TIME = new Set(['hour', 'minute', 'second', 'fraction'])

/**
 * A stored encounter instant, rendered at the precision it actually has.
 *
 * **UTC throughout, deliberately.** The instant was built by reading a zone-less
 * wall clock as UTC (see `encounter-event.ts` in the backend), so rendering it in
 * the browser's zone would shift the number Epic sent — a 13:45 admit would read
 * as 09:45 in New York. Reading it back in UTC returns exactly the characters
 * that arrived. This is the one place that inverse is applied.
 */
export function formatEncounterMoment(
  iso: string | null | undefined,
  precision: Precision,
): string | null {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  const date = d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  })
  if (!precision || !HAS_TIME.has(precision)) return date
  const time = d.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })
  return `${date}, ${time}`
}

/**
 * A provider as one readable name, or null when the message named nobody.
 *
 * `LASTNAME, FIRSTNAME` when both arrived, whichever one did when only one did,
 * and the bare id as a last resort — an id is still an answer to "who", where an
 * empty cell is not. The id is never appended to a name: PV1-7.1 is an Epic
 * internal number and means nothing to a coordinator reading the table.
 */
export function encounterProviderName(p: EncounterProvider | null | undefined): string | null {
  if (!p) return null
  const last = p.lastName?.trim()
  const first = p.firstName?.trim()
  if (last && first) return `${last}, ${first}`
  if (last || first) return (last || first) as string
  const id = p.id?.trim()
  return id ? id : null
}

/**
 * PV1-3 as one line: `MSWHC · 4W12 · Bed 01`.
 *
 * Point of care and facility are frequently the same string on MedStar's ADT
 * (`MSWHC4W` / `MSWHC`), so they are not both printed when one contains the
 * other — repeating it would fill the column without saying anything.
 */
export function encounterLocationLabel(e: {
  facility: string | null
  pointOfCare: string | null
  room: string | null
  bed: string | null
}): string | null {
  const parts: string[] = []
  const facility = e.facility?.trim()
  const poc = e.pointOfCare?.trim()
  if (facility) parts.push(facility)
  if (poc && !(facility && poc.startsWith(facility))) parts.push(poc)
  const room = e.room?.trim()
  if (room) parts.push(room)
  const bed = e.bed?.trim()
  if (bed) parts.push(`Bed ${bed}`)
  return parts.length > 0 ? parts.join(' · ') : null
}

/**
 * The status pill. Two states only, and they describe what EPIC thinks — a
 * discharged visit is closed at the hospital, which says nothing about any CGT
 * work still open for that patient.
 */
export const ENCOUNTER_STATUS_PILL: Record<
  PatientEncounter['status'],
  { label: string; className: string }
> = {
  active: { label: 'Admitted', className: 'bg-blue-100 text-blue-700' },
  discharged: { label: 'Discharged', className: 'bg-slate-100 text-slate-600' },
}

/**
 * Newest first, matching the order the API already returns.
 *
 * Re-sorted on the client anyway so the table cannot contradict the endpoint if
 * a caller ever merges two pages: a visit with no `admittedAt` (PV1-44 is
 * optional) falls back to when we first stored it, which is the only other
 * chronology we have.
 */
export function sortEncounters(rows: PatientEncounter[]): PatientEncounter[] {
  return [...rows].sort((a, b) => keyOf(b) - keyOf(a))
}

function keyOf(e: PatientEncounter): number {
  const t = e.admittedAt ? new Date(e.admittedAt).getTime() : NaN
  if (!Number.isNaN(t)) return t
  const c = new Date(e.createdAt).getTime()
  return Number.isNaN(c) ? 0 : c
}
